import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const StudentManager = () => {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [newStudent, setNewStudent] = useState({
    name: '',
    rate: '',
    teacher: '',
    parent: '',
    studentEmail: '',
    parentEmail: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [sRes, tRes] = await Promise.all([
      axiosInstance.get('/students'),
      axiosInstance.get('/teachers')
    ]);
    setStudents(sRes.data);
    setTeachers(tRes.data.filter(teacher => teacher.active));
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.teacher) return alert("Assign a teacher.");
    if (parseFloat(newStudent.rate) < 10) return alert("Rate ≥ $10.");
    if (newStudent.name.trim().length < 2) return alert("Enter valid name.");

    const payload = {
      ...newStudent,
      dateStart: new Date().toISOString().split('T')[0]
    };

    try {
      await axiosInstance.post('/students', payload);
      setNewStudent({
        name: '',
        rate: '',
        teacher: '',
        parent: '',
        studentEmail: '',
        parentEmail: '',
        active: true
      });
      fetchData();
      alert("Student registered!");
    } catch (err) {
      alert("Error: Name may be duplicate.");
    }
  };

  const openEditStudent = (student) => {
    setEditingStudent({ ...student });
  };

  const closeEditStudent = () => {
    setEditingStudent(null);
  };

  const saveStudentDetails = async () => {
    if (!editingStudent) return;

    try {
      await axiosInstance.patch(`/students/${editingStudent._id}`, {
        name: editingStudent.name,
        parent: editingStudent.parent,
        teacher: editingStudent.teacher,
        studentEmail: editingStudent.studentEmail || '',
        parentEmail: editingStudent.parentEmail || ''
      });

      fetchData();
      closeEditStudent();
      alert("Student details updated successfully!");
    } catch (err) {
      alert("Failed to update student: " + (err.response?.data?.message || err.message));
    }
  };

  const updateStudentRate = async (studentId, studentName) => {
    const newRate = prompt(`Enter new hourly rate for ${studentName}:`);

    if (!newRate || isNaN(newRate) || parseFloat(newRate) <= 0) {
      return alert("Please enter a valid rate.");
    }

    try {
      await axiosInstance.patch(`/students/${studentId}/rate`, {
        rate: parseFloat(newRate)
      });
      fetchData();
      alert("Rate updated successfully! New rate is effective immediately.");
    } catch (err) {
      alert("Failed to update rate: " + (err.response?.data?.message || err.message));
    }
  };

  const toggleStudentStatus = async (studentId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${action} this student?`)) return;

    try {
      await axiosInstance.patch(`/students/${studentId}`, {
        active: !currentStatus
      });
      fetchData();
      alert(`Student ${action}d successfully!`);
    } catch (err) {
      alert("Failed to update student status.");
      console.error(err);
    }
  };

  const deleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Permanently delete ${studentName}? This cannot be undone and will affect historical records.`)) return;

    try {
      await axiosInstance.delete(`/students/${studentId}`);
      fetchData();
      alert("Student deleted successfully.");
    } catch (err) {
      alert("Failed to delete student.");
      console.error(err);
    }
  };

  return (
    <div className="page-container">
      <h3>Student Roster & Assignments</h3>

      <form onSubmit={handleAddStudent} className="form-group student-form">
        <input
          placeholder="Student Name"
          value={newStudent.name}
          onChange={e => setNewStudent({ ...newStudent, name: e.target.value })}
          required
        />
        <input
          placeholder="Parent Name"
          value={newStudent.parent}
          onChange={e => setNewStudent({ ...newStudent, parent: e.target.value })}
          required
        />
        <input
          type="email"
          placeholder="Student Email (optional)"
          value={newStudent.studentEmail}
          onChange={e => setNewStudent({ ...newStudent, studentEmail: e.target.value })}
        />
        <input
          type="email"
          placeholder="Parent Email (optional)"
          value={newStudent.parentEmail}
          onChange={e => setNewStudent({ ...newStudent, parentEmail: e.target.value })}
        />
        <input
          type="number"
          placeholder="Hourly Rate ($)"
          value={newStudent.rate}
          onChange={e => setNewStudent({ ...newStudent, rate: e.target.value })}
          required
        />
        <select
          value={newStudent.teacher}
          onChange={e => setNewStudent({ ...newStudent, teacher: e.target.value })}
          required
        >
          <option value="">Assign Teacher...</option>
          {teachers.map(t => (
            <option key={t._id} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="submit" className="add-btn">Register Student</button>
      </form>

      <table className="roster-table">
        <thead>
          <tr>
            <th>Parent</th>
            <th>Student</th>
            <th>Student Email</th>
            <th>Parent Email</th>
            <th>Teacher</th>
            <th>Rate</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            // Group students by parent
            const studentsByParent = students.reduce((acc, student) => {
              const parent = student.parent;
              if (!acc[parent]) {
                acc[parent] = [];
              }
              acc[parent].push(student);
              return acc;
            }, {});

            // Sort parents alphabetically
            const sortedParents = Object.keys(studentsByParent).sort((a, b) =>
              a.localeCompare(b)
            );

            // Sort students within each parent group alphabetically
            sortedParents.forEach(parent => {
              studentsByParent[parent].sort((a, b) =>
                a.name.localeCompare(b.name)
              );
            });

            // Render rows
            return sortedParents.flatMap((parent, parentIndex) => {
              const parentStudents = studentsByParent[parent];

              return parentStudents.map((s, studentIndex) => {
                const isFirstStudentOfParent = studentIndex === 0;
                const rowSpan = parentStudents.length;

                return (
                  <tr key={s._id} className={!s.active ? 'inactive-row' : ''}>
                    {isFirstStudentOfParent && (
                      <td rowSpan={rowSpan} style={{
                        verticalAlign: 'middle',
                        fontWeight: 600,
                        backgroundColor: parentIndex % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
                        borderRight: '2px solid var(--input-border)',
                        color: 'var(--text)'
                      }}>
                        {parent}
                      </td>
                    )}
                    <td><strong>{s.name}</strong></td>
                    <td>{s.studentEmail || '-'}</td>
                    <td>{s.parentEmail || '-'}</td>
                    <td>{s.teacher}</td>
                    <td>
                      ${s.rate}/hr
                      {s.active && (
                        <button
                          onClick={() => updateStudentRate(s._id, s.name)}
                          style={{
                            marginLeft: '8px',
                            fontSize: '0.8rem',
                            padding: '2px 6px',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'transparent'
                          }}
                          title="Update rate"
                        >
                          ✏️
                        </button>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${s.active ? 'active' : 'retired'}`}>
                        {s.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => openEditStudent(s)}
                        className="submit-btn"
                        style={{ marginRight: '8px', fontSize: '0.85rem', padding: '4px 8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => toggleStudentStatus(s._id, s.active)}
                        className="status-toggle"
                        style={{ marginRight: '8px', fontSize: '0.85rem', padding: '4px 8px' }}
                      >
                        {s.active ? 'Deactivate' : 'Reactivate'}
                      </button>
                      {!s.active && (
                        <button
                          onClick={() => deleteStudent(s._id, s.name)}
                          className="delete-btn"
                          style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              });
            });
          })()}
        </tbody>
      </table>

      {/* Edit Student Modal */}
      {editingStudent && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Student Details</h3>

            <div className="form-group">
              <label>Student Name</label>
              <input
                type="text"
                value={editingStudent.name}
                onChange={e => setEditingStudent({ ...editingStudent, name: e.target.value })}
                placeholder="Student name"
              />
            </div>

            <div className="form-group">
              <label>Parent Name</label>
              <input
                type="text"
                value={editingStudent.parent}
                onChange={e => setEditingStudent({ ...editingStudent, parent: e.target.value })}
                placeholder="Parent name"
              />
            </div>

            <div className="form-group">
              <label>Student Email (optional)</label>
              <input
                type="email"
                value={editingStudent.studentEmail || ''}
                onChange={e => setEditingStudent({ ...editingStudent, studentEmail: e.target.value })}
                placeholder="Student email address"
              />
            </div>

            <div className="form-group">
              <label>Parent Email (optional)</label>
              <input
                type="email"
                value={editingStudent.parentEmail || ''}
                onChange={e => setEditingStudent({ ...editingStudent, parentEmail: e.target.value })}
                placeholder="Parent email address"
              />
            </div>

            <div className="form-group">
              <label>Assigned Teacher</label>
              <select
                value={editingStudent.teacher}
                onChange={e => setEditingStudent({ ...editingStudent, teacher: e.target.value })}
              >
                {teachers.map(t => (
                  <option key={t._id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="submit-btn" onClick={saveStudentDetails}>
                Save Changes
              </button>
              <button className="cancel-btn" onClick={closeEditStudent}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'var(--table-header-bg)', borderRadius: '8px' }}>
        <h4>ℹ️ Student Management Notes:</h4>
        <ul style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
          <li><strong>Rate Updates:</strong> New rate applies IMMEDIATELY from today forward. Previous sessions use historical rates.</li>
          <li><strong>Email Fields:</strong> Both student and parent emails are optional and can be left blank.</li>
          <li><strong>Teacher Assignment:</strong> You can reassign students to different teachers using the Edit button. Payroll will be assigned based on who actually teaches each session.</li>
          <li><strong>Deactivate:</strong> Inactive students cannot have new sessions logged but retain all historical data.</li>
          <li><strong>Delete:</strong> Only available after deactivating. Permanently removes student (use with caution).</li>
        </ul>
      </div>
    </div>
  );
};

export default StudentManager;